import React from 'react';
import { Box, Divider, Grid, KeyValue, Pill, SectionHeader } from 'web-library';
import { useTranslation } from 'react-i18next';

import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';
import { EntryProofDownloadModel } from '../../../../api/candidate/exam/model';
import DownloadSummaryPDFButton from '../../../../components/buttons/DownloadSummaryPDFButton';
import FormHeader from '../../../../components/form-items/FormHeader';
import EntryProofIcon from '../EntryProofIcon';
import { useStyles } from './style';
import { RegistrationType } from '../../../../api-refactored/entry-proof/useGetDownloadEntryProof';

const entryProofRender = (props: RegistrationHeaderDetailsProps, classes: Record<string,string>) => {
  const entryProofLength = props?.entryProofDownloadModels?.length;
  switch (true) {
    case entryProofLength === 0: {
      return <Box className={classes.entryProofContainer}>-</Box>;
    }
    case entryProofLength === 1: {
      const entryProofDownloadModels = props?.entryProofDownloadModels;
      if (entryProofDownloadModels && entryProofDownloadModels[0]) {
        const { fileName, uuid: fileUuid } = entryProofDownloadModels[0];
        if (fileName === null) {
          return <Box className={classes.entryProofContainer}>-</Box>;
        }
        return (
          <EntryProofIcon
            fileName={fileName}
            uuid={fileUuid}
            registrationType={RegistrationType.NON_NATIONAL}
          />
        );
      }
      return <Box className={classes.entryProofContainer}>-</Box>;
    }
    case (entryProofLength as number) > 1: {
      if (
        props?.entryProofDownloadModels?.every(
          (entryProof: EntryProofDownloadModel) => entryProof.fileName === null
        )
      ) {
        return <Box className={classes.entryProofContainer}>-</Box>;
      }
      return props?.entryProofDownloadModels?.map((entryProof: EntryProofDownloadModel) => {
        if (entryProof.fileName === null) {
          return null;
        }
        return (
          <EntryProofIcon
            fileName={entryProof.fileName}
            uuid={entryProof.uuid}
            key={entryProof.uuid}
            registrationType={RegistrationType.NON_NATIONAL}
          />
        );
      });
    }
    default: {
      return null;
    }
  }
};

interface RegistrationDetails {
  applicationID: string;
  applicationDateTime: string;
  applicationStatus: string;
  remarks: string;
}

interface RegistrationHeaderDetailsProps {
  applicationStatus: RegistrationDetails;
  onDownloadAction: () => void;
  isDownloading: boolean | undefined;
  entryProofDownloadModels?: EntryProofDownloadModel[];
}

const RegistrationHeaderDetails = ({ ...props }: RegistrationHeaderDetailsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const applicationDateTime = props.applicationStatus.applicationDateTime
    ? stringToLongDateAndShortTime(props.applicationStatus.applicationDateTime)
    : 'N/A';

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          <SectionHeader
            title={t('exam->>RegistrationSummary.RegistrationDetails')}
          >
            {DownloadSummaryPDFButton({
              isDownloading: props.isDownloading,
              onDownloadAction: props.onDownloadAction,
            })}
          </SectionHeader>
        </Grid>
        <Grid xs={12}>
          <Box className={classes.downloadContainer}>
            <Grid container direction="row">
              <Grid md={6} sm={12}>
                <KeyValue
                  keyText={t('exam->>RegistrationSummary.ApplicationId')}
                  valueText={props.applicationStatus.applicationID}
                />
              </Grid>
              <Grid md={6} sm={12} xs={12}>
                <KeyValue
                  keyText={t('exam->>RegistrationSummary.Status')}
                  valueText={
                    <div className={classes.statusContainer}>
                      <Grid className={classes.statusPill}>
                        <Pill
                          label={props.applicationStatus.applicationStatus}
                          variant={
                            props.applicationStatus.applicationStatus === 'REGISTERED'
                              ? 'positive'
                              : 'info'
                          }
                        />
                      </Grid>
                    </div>
                  }
                />
              </Grid>
            </Grid>

            <Grid container direction="row">
              <Grid md={6} sm={12}>
                <KeyValue
                  keyText={t('exam->>RegistrationSummary.ApplicationDateTime')}
                  valueText={applicationDateTime}
                />
              </Grid>
            </Grid>

            {(props.applicationStatus.remarks ||
              props.applicationStatus.applicationStatus === 'INCOMPLETE') && (
              <Grid container direction="row">
                <Grid md={6} sm={12}>
                  <KeyValue
                    keyText={t('exam->>RegistrationSummary.Remarks')}
                    valueText={props.applicationStatus.remarks || '-'}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
          <Divider />
        </Grid>
      </Grid>
      <FormHeader title={t('exam->>RegistrationSummary.EntryProof')} />
      {entryProofRender(props, classes)}
      <Box className={classes.divider}>
        <Divider />
      </Box>
    </>
  );
};

export default RegistrationHeaderDetails;
